<!--
  MarkdownInput.vue

  Form input for markdown fields. Read-only preview with an Expand button
  that opens the shared MarkdownEditModal. Ported from the legacy
  MarkdownField.vue with the parse/emit dance routed through `useField`.

  Used in compact contexts (sidebar field lists) where the full editor
  toolbar is too tall. For full inline editing (e.g. document body),
  import MarkdownEditor directly.
-->
<template>
  <div class="markdown-field">
    <div v-if="modelValue" class="preview" @click="openModal">
      <MarkdownEditor :model-value="modelValue as string" :editable="false" />
      <button class="btn btn-sm btn-ghost expand-btn" title="Edit" @click.stop="openModal">
        <font-awesome-icon icon="up-right-and-down-left-from-center" />
      </button>
    </div>
    <button v-else class="btn btn-light btn-sm placeholder-btn" @click="openModal">
      <font-awesome-icon icon="pen" class="mr-1" />
      {{ placeholder }}
    </button>

    <MarkdownEditModal
      ref="modal"
      :model-value="modelValue as string"
      @update:model-value="handleInput"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue"
import type { Field } from "@auger/core/fields"
import { useField } from "@auger/ui/fields/useField"
import MarkdownEditModal from "@/components/editors/MarkdownEditModal.vue"
import MarkdownEditor from "@/components/editors/MarkdownEditor.vue"

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    field: Field
    placeholder?: string
  }>(),
  {
    modelValue: "",
    placeholder: "Add content...",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
}>()

const { handleInput } = useField(props.field, emit)

const modal = ref<InstanceType<typeof MarkdownEditModal> | null>(null)

function openModal() {
  modal.value?.open()
}
</script>

<style>
.markdown-field {
  .preview {
    position: relative;
    max-height: 8rem;
    overflow: hidden;
    border: 1px solid var(--auger-border-light);
    border-radius: 4px;
    padding: 0.5rem;
    cursor: pointer;

    &:hover {
      border-color: var(--auger-border);
    }

    /* Mask bottom edge so truncated content fades */
    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 1.5rem;
      background: linear-gradient(transparent, var(--auger-bg));
      pointer-events: none;
    }

    .expand-btn {
      position: absolute;
      top: 0.25rem;
      right: 0.25rem;
      z-index: 1;
    }

    .markdown-editor .ProseMirror {
      padding: 0;
      min-height: auto;
      border: none;
      background: transparent;
    }
  }

  .placeholder-btn {
    width: 100%;
    justify-content: flex-start;
    color: var(--auger-text-muted);
  }
}
</style>
